// Toasts (HANDOFF §1, business handoff §4): ink background, radius 16,
// bottom-start, 46 px (54 px with undo). The undo variant drains a 3 px bar
// over 5 s and fires the action ONLY when the window closes (rule 8: «إنهاء»
// inline with a 5-second undo). Copy: «تم الحفظ.» «تم إنهاء موعد {name}» + «تراجع».

import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from 'react';
import { BrandMark } from '@/components/BrandMark';

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastOptions {
  /** ms before auto-dismiss (default 3500; 0 = sticky). */
  duration?: number;
  /** One trailing action, e.g. «عرض». */
  action?: ToastAction;
  /** 'success' = a document posted: the brand mark draws its check (board 21 success) before the message. */
  tone?: ToastTone;
}

export type ToastTone = 'default' | 'error' | 'success';

export interface UndoOptions {
  /** Fires when the window closes without an undo — do the real API call here. */
  onCommit: () => void;
  /** Fires when «تراجع» is pressed (restore the optimistic UI). */
  onUndo?: () => void;
  /** Window length in seconds (default 5). */
  seconds?: number;
  undoLabel?: string;
}

export interface ToastApi {
  toast: (message: string, opts?: ToastOptions) => number;
  /** Optimistic action with an undo window; returns the toast id. */
  undo: (message: string, opts: UndoOptions) => number;
  dismiss: (id: number) => void;
}

interface Item {
  id: number;
  message: string;
  action?: ToastAction;
  tone: ToastTone;
  undo?: { seconds: number; label: string; onUndo?: () => void; commit: () => void };
}

export const UNDO_SECONDS = 5;

const ToastCtx = createContext<ToastApi | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<Item[]>([]);
  const seq = useRef(0);
  const timers = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const t = timers.current.get(id);
    if (t) clearTimeout(t);
    timers.current.delete(id);
    setItems((xs) => xs.filter((x) => x.id !== id));
  }, []);

  const toast = useCallback(
    (message: string, opts: ToastOptions = {}) => {
      const id = ++seq.current;
      setItems((xs) => [...xs, { id, message, action: opts.action, tone: opts.tone ?? 'default' }]);
      const d = opts.duration ?? 3500;
      if (d > 0) timers.current.set(id, setTimeout(() => dismiss(id), d));
      return id;
    },
    [dismiss],
  );

  const undo = useCallback(
    (message: string, opts: UndoOptions) => {
      const id = ++seq.current;
      const seconds = opts.seconds ?? UNDO_SECONDS;
      let done = false;
      const commit = () => {
        if (done) return;
        done = true;
        dismiss(id);
        opts.onCommit();
      };
      setItems((xs) => [
        ...xs,
        {
          id,
          message,
          tone: 'default',
          undo: {
            seconds,
            label: opts.undoLabel ?? 'تراجع',
            commit,
            onUndo: () => {
              if (done) return;
              done = true;
              dismiss(id);
              opts.onUndo?.();
            },
          },
        },
      ]);
      timers.current.set(id, setTimeout(commit, seconds * 1000));
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const map = timers.current;
    return () => map.forEach((t) => clearTimeout(t));
  }, []);

  return (
    <ToastCtx.Provider value={{ toast, undo, dismiss }}>
      {children}
      <div className="e-toasts" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className="e-toast" data-undo={t.undo ? 'true' : undefined} data-tone={t.tone}>
            {t.tone === 'success' && <BrandMark mode="success" size={20} tone="white" />}
            <span className="e-toast-msg">{t.message}</span>
            {t.action && (
              <button
                type="button"
                className="e-toast-btn"
                onClick={() => {
                  t.action?.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
            {t.undo && (
              <>
                <button type="button" className="e-toast-btn" onClick={t.undo.onUndo}>
                  {t.undo.label}
                </button>
                <span className="e-toast-drain" style={{ animationDuration: `${t.undo.seconds}s` }} aria-hidden="true" />
              </>
            )}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastCtx);
  if (!ctx) throw new Error('useToast must be used inside <ToastProvider>');
  return ctx;
}
